import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { getToken, updateMe } from "@/lib/api";
import { SUPPORTED, getCachedDict, loadDict, resolve, setActiveDict } from "@/i18n/dictionaries";

const DEFAULT_LANG = "ca";
const STORAGE_KEY = "colygon_lang";
const RELOAD_FLAG = "colygon_i18n_reload";

const I18nContext = createContext(null);

const detectInitial = () => {
  const stored = (typeof window !== "undefined" && window.localStorage.getItem(STORAGE_KEY)) || null;
  if (stored && SUPPORTED.includes(stored)) return stored;
  if (typeof navigator !== "undefined") {
    const guess = (navigator.language || "es").slice(0, 2).toLowerCase();
    if (SUPPORTED.includes(guess)) return guess;
  }
  return DEFAULT_LANG;
};

export const I18nProvider = ({ children }) => {
  const [lang, setLangState] = useState(detectInitial);
  // Diccionario en pantalla. Al cambiar de idioma se mantiene el anterior
  // hasta que llega el nuevo, así no se ven claves sueltas mientras tanto.
  const [dict, setDict] = useState(() => getCachedDict(lang));

  useEffect(() => {
    let cancelled = false;
    const apply = (d) => {
      if (cancelled) return;
      setActiveDict(d);
      setDict(d);
      sessionStorage.removeItem(RELOAD_FLAG);
    };
    loadDict(lang)
      .catch(() => loadDict(lang)) // un reintento (red inestable)
      .then(apply)
      .catch(() => {
        // El chunk no llega (p. ej. es de un despliegue anterior): se recarga
        // una vez; si tampoco, la app sigue con los textos de respaldo.
        if (!sessionStorage.getItem(RELOAD_FLAG)) {
          sessionStorage.setItem(RELOAD_FLAG, "1");
          window.location.reload();
        } else {
          apply({});
        }
      });
    return () => {
      cancelled = true;
    };
  }, [lang]);

  useEffect(() => {
    document.documentElement.lang = lang;
    window.localStorage.setItem(STORAGE_KEY, lang);
  }, [lang]);

  const setLang = useCallback((newLang) => {
    if (!SUPPORTED.includes(newLang)) return;
    setLangState(newLang);
    // Persistir en el perfil para que los emails server-side (digest semanal,
    // radar…) salgan en este idioma. Solo si hay sesión (si no, sería un 401);
    // fire-and-forget para no bloquear el cambio de idioma en la UI.
    // Se persiste solo aquí (nunca en el montaje inicial), de modo que la
    // preferencia guardada nunca se sobrescribe antes de haberla leído.
    if (getToken()) {
      updateMe({ profile_lang: newLang }).catch(() => {});
    }
  }, []);

  const t = useCallback(
    (key, fallbackOrVars, varsOnly) => {
      let fallback = null;
      let vars = varsOnly;
      if (typeof fallbackOrVars === 'string') {
        fallback = fallbackOrVars;
      } else if (typeof fallbackOrVars === 'object') {
        vars = fallbackOrVars;
      }
      return resolve(dict, key, vars, fallback);
    },
    [dict]
  );

  const value = useMemo(
    () => ({ lang, setLang, t, supported: SUPPORTED }),
    [lang, setLang, t]
  );

  if (!dict) {
    // Solo la primera vez, mientras llega el diccionario (unos milisegundos).
    return (
      <div role="status" className="flex min-h-screen w-full items-center justify-center bg-[#F8FAFC]">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-slate-200 border-t-green-600" />
      </div>
    );
  }
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
};

export const useI18n = () => {
  const ctx = useContext(I18nContext);
  if (!ctx) {
    throw new Error("useI18n must be used inside <I18nProvider>");
  }
  return ctx;
};
